import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';
import ConfirmModal from '../../components/common/ConfirmModal';

import iconEdit from '../../assets/icon_edit.svg';
import iconDelete from '../../assets/icon_delete.svg';
import AppSpinner from '../../components/AppSpinner';

interface UserData {
  _id: string;
  userId: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: string;
  lastLoginDate?: string;
  status?: string;
  googleId?: string;
  kakaoId?: string;
  age?: number;
  gender?: string;
  phoneNumber?: string;
  referralCode?: string;
  referredBy?: string;
  lastLoginIp?: string;
}

const AdminUserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !id) return;

    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/admin/users/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser({ ...response.data, status: response.data.status || '정상' });
      } catch (err) {
        console.error('Failed to fetch user:', err);
        setError('사용자 정보를 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [isAuthenticated, id]);

  const getJoinPath = (user: UserData) => {
    const paths = [];
    if (user.kakaoId)
      paths.push(
        <span key="kakao" className="badge-sns kakao">
          카카오
        </span>
      );
    if (user.googleId)
      paths.push(
        <span key="google" className="badge-sns google">
          구글
        </span>
      );
    if (!user.kakaoId && !user.googleId)
      paths.push(
        <span key="signup" className="badge-sns signup">
          회원가입
        </span>
      );
    return <div className="join-paths">{paths}</div>;
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      navigate('/admin/users');
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('삭제에 실패했습니다.');
    }
  };

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;
  if (!user) return <div className="admin-page">사용자를 찾을 수 없습니다.</div>;

  return (
    <div className="admin-page">
      <h1>사용자 정보</h1>

      <div className="detail-table-container">
        <table className="detail-table">
          <colgroup>
            <col style={{ width: '120px' }} />
            <col />
            <col style={{ width: '120px' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th>이름</th>
              <td colSpan={3}>{user.displayName}</td>
            </tr>
            <tr>
              <th>아이디</th>
              <td colSpan={3}>{user.userId || '-'}</td>
            </tr>
            <tr>
              <th>이메일</th>
              <td colSpan={3}>{user.email}</td>
            </tr>
            <tr>
              <th>상태</th>
              <td>
                <span className={`status-badge ${user.status === '정상' ? 'normal' : 'suspended'}`}>
                  {user.status}
                </span>
              </td>
              <th>가입경로</th>
              <td>{getJoinPath(user)}</td>
            </tr>
            <tr>
              <th>가입일</th>
              <td>
                {new Date(user.createdAt).toLocaleDateString()}{' '}
                {new Date(user.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
              <th>최근 접속일</th>
              <td>{user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleString() : '-'}</td>
            </tr>
            <tr> 
              <th>나이</th>
              <td>{user.age || '-'}</td>
              <th>성별</th>
              <td>{user.gender === 'male' ? '남자' : '여자'}</td>
            </tr>
            <tr>
              <th>휴대폰 번호</th>
              <td>{user.phoneNumber || '-'}</td>
              <th>추천인 코드</th>
              <td>{user.referralCode || '-'}</td>
            </tr>
            <tr>
              <th>접근 IP</th>
              <td>{user.lastLoginIp || '-'}</td>
              <th>디바이스 정보</th>
              <td>{'-'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bottom-actions right-align">
        <button className="btn-list" onClick={() => navigate('/admin/users')}>
          ≡ 목록
        </button>
        <div className="action-buttons">
          <button className="btn-delete" onClick={() => setIsDeleteModalOpen(true)}>
            <img src={iconDelete} alt="delete" /> 삭제
          </button>
          <button className="btn-edit" onClick={() => navigate(`/admin/users/${id}/edit`)}>
            <img src={iconEdit} alt="edit" /> 수정
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="삭제하시겠습니까?"
        message="해당 항목을 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default AdminUserDetailPage;
